import { PartnerLaunchEnv } from './partner-launch-sandbox';

export const PARTNER_ERROR_CODES = ['launch_expired', 'launch_used', 'launch_rejected', 'tenant_not_active', 'session_ended'] as const;
export type PartnerErrorCode = typeof PARTNER_ERROR_CODES[number];

export const LAUNCH_TIMEOUT_MS = 60000;
export const LAUNCH_READY_INTERVAL_MS = 1000;
export const PARTNER_SESSION_ENDED_EVENT = 'mona-partner-session-ended';
export const MAX_FILE_CHUNKS = 11;

export const NOT_FRAMED_TEXT = 'MONA kann nur innerhalb Ihrer HR4YOU-Oberfläche geöffnet werden.';

export const PARTNER_ERROR_TEXT: Record<PartnerErrorCode, string> = {
  launch_expired: 'Der Startlink ist abgelaufen. Bitte öffnen Sie MONA erneut aus HR4YOU.',
  launch_used: 'Dieser Startlink wurde bereits verwendet. Bitte öffnen Sie MONA erneut aus HR4YOU.',
  launch_rejected: 'MONA konnte nicht gestartet werden. Bitte öffnen Sie MONA erneut aus HR4YOU.',
  tenant_not_active: 'MONA ist für Ihr Unternehmen derzeit nicht aktiv.',
  session_ended: 'Ihre MONA-Sitzung ist beendet. Bitte öffnen Sie MONA erneut aus HR4YOU.'
};

export interface PartnerDocument {
  version: string;
  title: string;
  url: string;
  sha256: string;
}

export interface PartnerSessionPayload {
  partner: string;
  production: boolean;
  plan?: string;
  tenantState: string;
  role: 'admin' | 'member';
  company: string;
  user: { id: string; email: string; firstName: string; lastName: string };
  fileHosts: string[];
  consentRequired: boolean;
  documents: Record<string, PartnerDocument> | null;
}

export interface PartnerRedeemResponse extends PartnerSessionPayload {
  customToken: string;
  sessionExpiresAt: string;
  language?: string;
  mode?: string;
  context?: Record<string, unknown> | null;
  parentOrigin: string;
}

export class PartnerEmbedError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

export function parseEmbedOrigins(content: string | null | undefined): string[] {
  const origins: string[] = [];
  for (const entry of String(content || '').split(/\s+/)) {
    if (!entry) continue;
    try {
      const url = new URL(entry);
      if (url.protocol === 'https:' && url.origin === entry && !origins.includes(entry)) origins.push(entry);
    } catch {}
  }
  return origins;
}

export function readEmbedOrigins(doc: Document = document): string[] {
  const meta = doc.querySelector('meta[name="mona-embed-origins"]');
  return parseEmbedOrigins(meta ? meta.getAttribute('content') : null);
}

export function partnerApiBase(env: PartnerLaunchEnv): string {
  return env === 'live' ? 'https://api.mona-ai.cloud' : 'https://dev.api.mona-ai.cloud';
}

export function launchMatchesHost(env: PartnerLaunchEnv, hostname: string): boolean {
  if (hostname === 'app.mona-ai.io') return env === 'live';
  if (hostname === 'sandbox-mona-agents.web.app' || hostname === 'sandbox.app.mona-ai.io') return env === 'test';
  return env === 'test' && (hostname === 'localhost' || hostname === '127.0.0.1');
}

export function toPartnerErrorCode(code: unknown, fallback: PartnerErrorCode = 'launch_rejected'): PartnerErrorCode {
  return (PARTNER_ERROR_CODES as readonly string[]).includes(code as string) ? code as PartnerErrorCode : fallback;
}

export function isFramed(win: Window = window): boolean {
  try {
    return win.parent !== win && win.self !== win.top;
  } catch {
    return true;
  }
}

export async function partnerEmbedRequest<T>(
  base: string,
  path: string,
  options: { method?: 'GET' | 'POST'; idToken?: string | null; body?: unknown; fetchFn?: FetchFn } = {}
): Promise<T> {
  const method = options.method || 'POST';
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (options.idToken) headers['Authorization'] = `Bearer ${options.idToken}`;
  const fetchFn: FetchFn = options.fetchFn || ((input, init) => fetch(input, init));
  let response: Response;
  try {
    response = await fetchFn(`${base}/ars/partner-embed${path}`, {
      method,
      headers,
      body: method === 'GET' ? undefined : JSON.stringify(options.body || {}),
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer'
    });
  } catch {
    throw new PartnerEmbedError('network_error', 'MONA ist gerade nicht erreichbar.', 0);
  }
  let data: any = null;
  try {
    data = await response.json();
  } catch {}
  if (!response.ok || !data || data.success !== true) {
    const error = (data && data.error) || {};
    throw new PartnerEmbedError(String(error.code || 'request_failed'), String(error.message || `HTTP ${response.status}`), response.status);
  }
  return data as T;
}

export function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export async function fetchPartnerFile(base: string, idToken: string, url: string, fetchFn?: FetchFn): Promise<File> {
  const meta = await partnerEmbedRequest<any>(base, '/file', { idToken, body: { url }, fetchFn });
  const chunks = Number(meta.chunks);
  if (!Number.isInteger(chunks) || chunks < 1 || chunks > MAX_FILE_CHUNKS) {
    throw new PartnerEmbedError('invalid_file', 'Die Datei konnte nicht geladen werden.', 0);
  }
  const parts: Uint8Array[] = [];
  for (let index = 0; index < chunks; index++) {
    const chunk = await partnerEmbedRequest<any>(base, '/file/chunk', { idToken, body: { fileId: meta.fileId, index }, fetchFn });
    parts.push(base64ToBytes(String(chunk.base64 || '')));
    if (chunk.last) break;
  }
  return new File(parts, String(meta.name || 'dokument'), { type: String(meta.mimeType || 'application/octet-stream') });
}

export function acceptedDocuments(documents: Record<string, PartnerDocument> | null): Record<string, { version: string; sha256: string }> {
  const accepted: Record<string, { version: string; sha256: string }> = {};
  Object.entries(documents || {}).forEach(([key, doc]) => {
    accepted[key] = { version: doc.version, sha256: doc.sha256 };
  });
  return accepted;
}
